import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { HambergerMenu } from 'iconsax-react';
import { useAppSelector } from '@application/store/useStore';
import { colCenter } from '@ui/style/style';
import EventRegistrationLanguageSwitcher from './EventRegistrationLanguageSwitcher';
import { useEventRegistrationLanguageContext } from './EventRegistrationLanguageContext';
import { useEventRegistrationTranslation } from './hooks/useEventRegistrationTranslation';
import { useEventRegistrationDrawerContext } from './EventRegistrationDrawerContext';
import { useEventRegistrationHeaderLogo } from './hooks/useEventRegistrationHeaderLogo';
const EventRegistrationHeader = ({ hideNavLinks = false, embeddedPreview = false, previewLayout }) => {
    const theme = useTheme();
    const { t, activeLanguageCode } = useEventRegistrationTranslation();
    const isSmallScreen = useMediaQuery('(max-width:901px)');
    const isBelow390 = useMediaQuery('(max-width:391px)');
    const isBelow1024 = useMediaQuery('(max-width:1025px)');
    const { registrationCategories } = useAppSelector((state) => state.faqCategory);
    const hasFaqs = registrationCategories.some((c) => c.questions && c.questions.length > 0);
    const { eventLanguages, selectedLanguageId, handleLanguageChange } = useEventRegistrationLanguageContext();
    const { openMobileMenu, openContactUs, openFaqs, openSummary, showSummaryPreviewButton } = useEventRegistrationDrawerContext();
    const isPreviewMobile = embeddedPreview && previewLayout === 'mobile';
    const isPreviewTablet = embeddedPreview && previewLayout === 'tablet';
    const isPreviewCompact = isPreviewMobile || isPreviewTablet;
    const useCompactHeader = embeddedPreview ? isPreviewCompact : isSmallScreen;
    const useNarrowHeader = embeddedPreview ? isPreviewMobile : isBelow390;
    const useShortHeader = embeddedPreview ? isPreviewCompact : isBelow1024;
    const languageCode = String(activeLanguageCode).toLowerCase();
    const partOfVdkLogoSrc = languageCode.startsWith('de')
        ? '/assets/images/vdkLogo-de.svg'
        : languageCode.startsWith('nl')
            ? '/assets/images/vdkLogo-nl.svg'
            : '/assets/images/vdkLogo-eng.svg';
    const { headerLogo, showPartOfVdk } = useEventRegistrationHeaderLogo(embeddedPreview);
    const navLinks = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'text', color: 'inherit', onClick: openContactUs, sx: { color: '#00244D', textTransform: 'none', fontWeight: 500, whiteSpace: 'nowrap' }, children: t('eventRegistration.contactUs') }), hasFaqs && (_jsx(Button, { variant: 'text', color: 'inherit', onClick: openFaqs, sx: { color: '#00244D', textTransform: 'none', fontWeight: 500, whiteSpace: 'nowrap' }, children: t('eventRegistration.faqs') }))] }));
    return (_jsxs(Box, { sx: {
            boxShadow: '0px 4px 0px 0px #2423470A',
            position: 'sticky',
            background: '#FFFFFF',
            borderRadius: embeddedPreview ? { xs: 2, md: 3 } : { xs: 2, md: 3 },
            py: embeddedPreview ? (isPreviewCompact ? 1 : 1.5) : 2,
            px: useNarrowHeader ? 1 : embeddedPreview ? 1.5 : 2,
            zIndex: 20,
            height: embeddedPreview
                ? useShortHeader
                    ? 56
                    : 72
                : useShortHeader
                    ? 72
                    : 80,
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            ...(embeddedPreview
                ? {
                    mx: 0,
                    top: 0,
                    width: '100%',
                    alignSelf: 'stretch'
                }
                : {
                    [theme.breakpoints.up(1024)]: {
                        mx: 3,
                        top: 24,
                        width: 'calc(100% - 48px)'
                    },
                    [theme.breakpoints.down(1025)]: {
                        mx: { xs: 0, sm: 1 },
                        top: { xs: 0, sm: 12 },
                        width: { xs: '100%', sm: 'calc(100% - 16px)' }
                    },
                    [theme.breakpoints.down(391)]: {
                        mx: 0,
                        top: 0,
                        width: '100%',
                        borderTopRightRadius: 0,
                        borderTopLeftRadius: 0
                    }
                })
        }, children: [_jsxs(Box, { sx: {
                    ...colCenter,
                    flexDirection: 'row',
                    gap: showPartOfVdk ? (useNarrowHeader ? 1 : 1.5) : 0,
                    minWidth: 0
                }, children: [_jsx(Box, { component: 'img', src: headerLogo.src, alt: headerLogo.alt, sx: {
                            maxHeight: isPreviewMobile ? 28 : isPreviewTablet ? 32 : { xs: 32, md: 40 },
                            maxWidth: isPreviewMobile ? 96 : isPreviewTablet ? 120 : { xs: 120, sm: 200 },
                            width: 'auto',
                            objectFit: 'contain'
                        } }), showPartOfVdk && (_jsx(Box, { sx: { ...colCenter, flexDirection: 'row', gap: 1 }, children: _jsx(Box, { component: 'img', src: partOfVdkLogoSrc, alt: 'VDK', sx: { height: isPreviewCompact ? 14 : { xs: 16, md: 21 }, width: 'auto' } }) }))] }), _jsxs(Box, { sx: { ...colCenter, flexDirection: 'row', gap: { xs: 1, md: 2 }, minWidth: 0 }, children: [!hideNavLinks && !useCompactHeader && navLinks, !useCompactHeader ? (_jsx(EventRegistrationLanguageSwitcher, { eventLanguages: eventLanguages, selectedLanguageId: selectedLanguageId, onLanguageChange: handleLanguageChange })) : (_jsxs(Box, { sx: { ...colCenter, flexDirection: 'row', gap: 1 }, children: [showSummaryPreviewButton && (_jsx(Button, { variant: 'outlined', size: 'small', onClick: openSummary, sx: {
                                    borderColor: '#00244D33',
                                    color: '#00244D',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.75
                                }, children: t('eventRegistration.form.previewSummary', { defaultValue: 'Preview' }) })), _jsx(IconButton, { "aria-label": 'Open menu', onClick: openMobileMenu, sx: {
                                    width: 40,
                                    height: 40,
                                    border: '1px solid #00244D33',
                                    borderRadius: 1.5,
                                    color: '#00244D'
                                }, children: _jsx(HambergerMenu, { size: 20 }) })] }))] })] }));
};
export default EventRegistrationHeader;
